import React, { useEffect, useState } from "react";
import { capture } from "../../../analytics";
import { gql, useQuery, useMutation } from "@apollo/client";

const MY_SUBSCRIPTION_STATUS = gql`
  query MySubscriptionStatus {
    mySubscriptionStatus {
      hasActiveSubscription
      status
      currentPeriodEnd
      cancelAtPeriodEnd
      productName
      amount
      currency
    }
  }
`;

const CREATE_MY_CHECKOUT_SESSION = gql`
  mutation CreateMyCheckoutSession($returnUrl: String!) {
    createMyCheckoutSession(returnUrl: $returnUrl)
  }
`;

const CREATE_MY_PORTAL_SESSION = gql`
  mutation CreateMyPortalSession($returnUrl: String!) {
    createMyPortalSession(returnUrl: $returnUrl)
  }
`;

// Season Pass (#307): shown on the Account tab when the caller holds
// one, so "when does my season end" has an answer here too.
const MY_SEASON_PASS = gql`
  query MySeasonPassForAccount {
    mySeasonPass {
      active
      until
    }
  }
`;

// Self-service Stripe subscription. Renders a Subscribe button when
// no active subscription exists, a Manage Subscription button (linking
// to the Stripe Customer Portal) when one does. All Stripe state lives
// at Stripe; this component just surfaces a thin status read for the
// "active / canceled at period end / past due" display.
//
// Used by both UserDashboard's Account tab (regular users) and
// AdminLogin's Account tab (admins/captains). Extracted to a shared
// file so the same UI shows up on both surfaces.
//
// Optional `sex` prop (UserDashboard passes it): when there is NO
// active subscription, the subscribe CTA only renders for sex="male" —
// women get free access, and users who haven't answered the t-shirt/sex
// question yet shouldn't see pricing that may not apply to them. An
// ACTIVE subscription always renders its manage UI regardless. Omit
// the prop (AdminLogin does) for the ungated legacy behavior.
const MySubscriptionSection = ({ sex }) => {
  const { data, loading, error, refetch } = useQuery(MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: "network-only",
  });
  const [createCheckout, { loading: checkoutLoading }] = useMutation(
    CREATE_MY_CHECKOUT_SESSION
  );
  const [createPortal, { loading: portalLoading }] = useMutation(
    CREATE_MY_PORTAL_SESSION
  );
  const [actionError, setActionError] = useState("");
  const { data: passData } = useQuery(MY_SEASON_PASS, {
    fetchPolicy: "network-only",
  });
  const pass = passData?.mySeasonPass;
  const passUntil =
    pass?.active && pass?.until ? new Date(pass.until) : null;

  // Returning from Stripe ?subscribed=1 means the checkout completed;
  // refetch immediately so the UI flips from Subscribe to Manage
  // without a manual refresh.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "1") {
      capture("subscription_completed");
      refetch();
      // Clean the query param so re-mounts don't re-trigger.
      const url = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", url);
    }
  }, [refetch]);

  const returnUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "https://activebeaches.com/dashboard";

  const goCheckout = async () => {
    capture("checkout_started", { source: "account_tab" });
    setActionError("");
    try {
      const { data: out } = await createCheckout({ variables: { returnUrl } });
      const url = out?.createMyCheckoutSession;
      if (url) {
        window.location.href = url;
      } else {
        setActionError("Stripe returned no checkout URL.");
      }
    } catch (err) {
      setActionError(err.message || "Couldn't start checkout.");
    }
  };

  const goPortal = async () => {
    setActionError("");
    try {
      const { data: out } = await createPortal({ variables: { returnUrl } });
      const url = out?.createMyPortalSession;
      if (url) {
        window.location.href = url;
      } else {
        setActionError("Stripe returned no portal URL.");
      }
    } catch (err) {
      setActionError(err.message || "Couldn't open portal.");
    }
  };

  // Wrapper uses inline styles only -- no shared CSS so the component
  // looks the same regardless of the surrounding page's stylesheet.
  const wrapStyle = {
    marginTop: 16,
    padding: 16,
    background: "#f5f7fa",
    borderRadius: 8,
    border: "1px solid #e3e6ea",
  };
  const rowStyle = { margin: "6px 0", color: "#333" };

  if (loading && !data) {
    return (
      <div style={wrapStyle}>
        <p style={rowStyle}>Loading subscription…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ ...wrapStyle, color: "#b00020" }}>
        Couldn't load subscription: {error.message}
      </div>
    );
  }
  const status = data?.mySubscriptionStatus;
  const active = !!status?.hasActiveSubscription;
  // Sex gating (only when the caller passes the prop): hide the
  // subscribe CTA until the t-shirt/sex question says "male". Active
  // subscriptions still show so cancel/manage is never blocked.
  if (!active && sex !== undefined) {
    const s = String(sex || "").trim().toLowerCase();
    if (s !== "male") return null;
  }
  const renewsOrEndsAt = status?.currentPeriodEnd
    ? new Date(status.currentPeriodEnd * 1000).toLocaleDateString()
    : null;
  const amountDisplay =
    status?.amount != null && status?.currency
      ? `$${(status.amount / 100).toFixed(2)} ${status.currency.toUpperCase()}`
      : null;
  const submitting = checkoutLoading || portalLoading;

  return (
    <div style={wrapStyle}>
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>Subscription</h3>
      {active ? (
        <>
          <p style={rowStyle}>
            <strong>Status:</strong> {status.status}
            {status.cancelAtPeriodEnd && " — cancels at period end"}
          </p>
          {status.productName && (
            <p style={rowStyle}>
              <strong>Plan:</strong> {status.productName}
              {amountDisplay ? ` (${amountDisplay} / month)` : ""}
            </p>
          )}
          {renewsOrEndsAt && (
            <p style={rowStyle}>
              <strong>
                {status.cancelAtPeriodEnd ? "Ends" : "Renews"}:
              </strong>{" "}
              {renewsOrEndsAt}
            </p>
          )}
          <button
            type="button"
            onClick={goPortal}
            disabled={submitting}
            style={{
              padding: "8px 16px",
              background: "#4a89dc",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontSize: 14,
              cursor: submitting ? "default" : "pointer",
              fontWeight: 600,
              marginTop: 8,
            }}
          >
            {portalLoading ? "Opening…" : "Manage subscription"}
          </button>
        </>
      ) : (
        <>
          {passUntil && !Number.isNaN(passUntil.getTime()) && (
            <p style={rowStyle}>
              <strong>
                <span role="img" aria-label="ticket">🎟️</span> Season Pass:
              </strong>{" "}
              active through{" "}
              {passUntil.toLocaleDateString()}. Members get the next season
              free — subscribe below to keep playing after it ends.
            </p>
          )}
          <p style={{ ...rowStyle, color: "#555" }}>
            You don't have an active subscription. Subscribe for full
            access to event locations and the GroupMe chat.
          </p>
          <button
            type="button"
            onClick={goCheckout}
            disabled={submitting}
            style={{
              padding: "10px 20px",
              background: "#1f7a3a",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontSize: 15,
              cursor: submitting ? "default" : "pointer",
              fontWeight: 600,
              marginTop: 8,
            }}
          >
            {checkoutLoading ? "Opening Stripe…" : "Subscribe"}
          </button>
          {status?.status === "canceled" && (
            <p style={{ color: "#888", fontSize: 12, marginTop: 8 }}>
              Previous subscription was canceled — subscribing again
              starts a fresh one against the same Stripe customer.
            </p>
          )}
        </>
      )}
      {actionError && (
        <p style={{ color: "#b00020", marginTop: 8 }}>{actionError}</p>
      )}
      <p style={{ color: "#888", fontSize: 12, marginTop: 8 }}>
        Billing is handled by Stripe. Update card, change plan, or
        cancel from the Stripe Customer Portal (button above).
      </p>
    </div>
  );
};

export default MySubscriptionSection;
